import React, { useState } from "react";
import { getStrings } from "../../Shared/Reservation/StringResources";
import { useDispatch } from "react-redux";
import { Alert, Badge, Button } from "react-bootstrap";
import { OptionList, SegmentInformation, TravelBaseInformation } from "../Components/Booking/TravelSummary";
import dayjs from "dayjs";
import { addTableBooking, cancelTableBooking } from "./Actions";
import { ITableBooking, useStateSelector } from "./Model";
import { Direction, IServiceInformation, ITravelSegment } from "../Models";
import { getSettings } from "../Settings";
import { BookMealsModal } from "./BookMealsModal";
import { BookTableModal } from "./BookTableModal";
import SvgTrash from "../../Shared/Icons/bootstrap/Trash";
import { getKrogenProductCodes } from "../TravelDetails/KrogenOptionsHelpers";

const stringResources = getStrings().component;
const orderStrings = stringResources.orderSummary;
const tableBookingStrings = stringResources.tableBooking;
const mealBookingStrings = stringResources.tableBooking.mealBookingDialog;
const commonStrings = stringResources.common;
const krogenProductCodes = getKrogenProductCodes();


export const TravelSummary = (props: { direction: Direction, onMealsBooked?: () => void }) => {

	const dispatch = useDispatch();

	const booking = useStateSelector(state => state.manageBooking.booking);

	if (!booking)
		return null;

	const services = useStateSelector(state => state.manageBooking.services);

	const travel = props.direction === "outbound" ? booking?.outbound : booking?.inbound;
	const tableBookings = booking?.tableBooking?.bookings;

	const departure = dayjs(travel.departure);

	const settings = getSettings();
	const enableTableBooking = (!settings?.lastTableBookingDate || departure.isSameOrBefore(settings.lastTableBookingDate, "d")) && !travel.hasArrived;

	const [bokaBordSegment, setBokaBordSegment] = useState<ITravelSegment>();
	const [bokaMåltider, setBokaMåltider] = useState(false);
	const [ändradeSegment, setÄndradeSegment] = useState<ITravelSegment[]>([]);

	const bokaBordClosed = (booking?: ITableBooking) => {
		console.debug("bokaBordClosed", booking);
		setBokaBordSegment(null);
		if (booking)
			dispatch(addTableBooking(booking))

		setBokaMåltider(false);
	};

	const bokaMåltiderClicked = (segment: ITravelSegment) => {
		console.debug("bokaMåltiderClicked", segment);
		setBokaMåltider(true);
		setBokaBordSegment(segment);
	}

	const bokaMåltiderClosed = (optionsChanged: boolean) => {
		console.debug("bokaMåltiderClosed", optionsChanged);

		if (bokaBordSegment)
		{

			if (!ändradeSegment.some(x => x.segmentId === bokaBordSegment.segmentId))
			{
				let temp = [...ändradeSegment];
				temp.push(bokaBordSegment);
				setÄndradeSegment(temp);
			}

			setBokaBordSegment(null);
		}

		setBokaMåltider(false);
		if (optionsChanged && props.onMealsBooked)
			props.onMealsBooked();
	};


	return <div className={travel.cancelled ? "text-muted" : ""}>
		<TravelBaseInformation travel={travel} direction={props.direction} />

		{travel.segments?.map((segment, i) => <React.Fragment key={i}>
			{ i > 0 && <hr /> }
			<Segment bookingNumber={booking.bookingNumber} segment={segment} travelCancelled={travel.cancelled} tableBookings={tableBookings} serviceInformation={services?.find(x => x.name === segment.serviceName)}
				enableTableBooking={enableTableBooking} enableMealBooking={settings?.enableMealBooking} onBokaBordClick={() => setBokaBordSegment(segment)} onBokaMåltiderClick={() => bokaMåltiderClicked(segment)} />
		</React.Fragment>)}

		{ enableTableBooking && <>
			<BookTableModal show={!bokaMåltider} bookingNumber={booking.bookingNumber} direction={props.direction} segment={bokaBordSegment} maxPassengerCount={booking.passengers.length} onClosed={bokaBordClosed} />
			<BookMealsModal show={bokaMåltider} bookingNumber={booking.bookingNumber} direction={props.direction} segment={bokaBordSegment} maxPassengerCount={booking.passengers.length} onClosed={bokaMåltiderClosed} />
		</>}
	</div>;
};

const Segment = (props: { bookingNumber: string, segment: ITravelSegment, travelCancelled?: boolean, serviceInformation?: IServiceInformation, enableTableBooking: boolean, enableMealBooking: boolean; tableBookings?: ITableBooking[],
	onBokaBordClick: () => void, onBokaMåltiderClick: () => void}) => {

	const segment = props.segment;
	const tableBookings = props.tableBookings?.filter(x => x.segmentId === segment.segmentId);
	const duration = dayjs.duration(segment.duration);

	const maxTableBookings = props.enableMealBooking ? 1 : Math.min(duration.asHours() >= 1 && Math.ceil(duration.asHours() / 4), 4); //Max en bokning per 4 timmar, max 4 bokningar totalt. Inga bokningar på resor under 1 timme.
	const numberOfTableBookings = tableBookings?.length || 0;

	const mealBookingServiceNames = getSettings().mealBookingServiceNames || [];
	const isMealBookingService = mealBookingServiceNames.includes(segment.serviceName);
	//Vi vet inte om det finns Krog på annat sätt än att begränsa serviceType till STTRAIN eller STNIGHT
	const enableTableBooking = props.enableTableBooking && !props.travelCancelled && (props.segment.serviceType.code === "STTRAIN" || props.segment.serviceType.code === "STNIGHT");


	const tableBookingOnly = enableTableBooking && !isMealBookingService;
	const mealBooking = enableTableBooking && props.enableMealBooking && isMealBookingService;
	const allowTableBookings = duration.asHours() >= 1 && numberOfTableBookings < maxTableBookings;

	return <div className="segment-summary">
		<SegmentInformation segment={props.segment} />


		{tableBookingOnly && <TableBookings bookingNumber={props.bookingNumber} segment={segment} tableBookings={tableBookings} onBokaBordClick={props.onBokaBordClick} allowBookings={allowTableBookings} />}
		{mealBooking && <MealBooking bookingNumber={props.bookingNumber} segment={segment} tableBookings={tableBookings} onBokaBordClick={props.onBokaMåltiderClick} />}
	</div>
}

const TableBookings = (props: { bookingNumber: string, segment: ITravelSegment, tableBookings?: ITableBooking[], allowBookings: boolean, onBokaBordClick: () => void } ) => {

	const dispatch = useDispatch();

	const [cancelling, setCancelling] = useState<number>();

	const tableBookings = props.tableBookings?.sort((a, b) => dayjs(`${a.date}T${a.time}`).isBefore(`${b.date}T${b.time}`) ? -1 : 1);

	const cancelClick = (booking: ITableBooking) => {
		console.debug("cancelClick", booking);

		if (!confirm(tableBookingStrings.cancelConfirm + " " + booking.time + "?"))
			return;

		setCancelling(booking.bookingId)
		dispatch(cancelTableBooking(props.bookingNumber, booking.bookingId));
	}

	return <>
		<strong><i className="travelicon-KROG" />{orderStrings.tableBookingsHeading}</strong><br />

		{tableBookings?.map(tableBooking => <div key={tableBooking.bookingId} className={cancelling === tableBooking.bookingId ? "text-muted" : ""}>
			{tableBooking.time}, {tableBooking.persons} {tableBooking.persons > 1 ? commonStrings.persons : commonStrings.person}
			<Button disabled={cancelling === tableBooking.bookingId} size="sm" variant="icon" className="ml-3" title={tableBookingStrings.cancel} onClick={() => cancelClick(tableBooking)}><SvgTrash /></Button>
		</div>)}

		{ props.allowBookings && <Button variant="outline-primary" className="mt-2" onClick={props.onBokaBordClick}>{tableBookingStrings.submitText}</Button> }
	</>

};


const MealBooking = (props: { bookingNumber: string, segment: ITravelSegment, tableBookings?: ITableBooking[], onBokaBordClick: () => void } ) => {

	const tableBookings = props.tableBookings?.sort((a, b) => dayjs(`${a.date}T${a.time}`).isBefore(`${b.date}T${b.time}`) ? -1 : 1);
	const krogenOptions = props.segment.options?.filter(x => krogenProductCodes.includes(x.code)) || [];
	const hasProvisionalOptions = krogenOptions.some(x => x.provisionalCount);
	const hasNonProvisionalOptions = krogenOptions.some(x => !x.provisionalCount);

	const allowChanges = getSettings().enableEditMealBooking;

	const showBokaButton = !hasProvisionalOptions && (allowChanges || !hasNonProvisionalOptions);

	return <>
		<h4><i className="travelicon-KROG" />{mealBookingStrings.title}</h4>

		{ krogenOptions?.length > 0 && <>
			<strong>{mealBookingStrings.bookedMealsTitle}</strong>
			<OptionList options={krogenOptions} />
		</>}

		{ tableBookings?.length > 0 && <>
			<strong>{mealBookingStrings.bookedTablesTitle}</strong>
			{tableBookings?.map(tableBooking => <div key={tableBooking.bookingId}>
				{tableBooking.time}, {tableBooking.persons} {tableBooking.persons > 1 ? commonStrings.persons : commonStrings.person} {tableBooking.temporary && <Badge variant="warning" className="ml-2">{commonStrings.temporary}</Badge>}
			</div>)}
		</>}

		{ !(krogenOptions?.length > 0 || tableBookings?.length > 0) && <Alert variant="info">{mealBookingStrings.mealBookingInfo}</Alert>}

		{ hasNonProvisionalOptions && !hasProvisionalOptions && <Alert variant="info">{mealBookingStrings.editDisabled}</Alert> }
		{ showBokaButton && <Button variant="outline-primary" className="mb-2 mt-2" onClick={props.onBokaBordClick}>{mealBookingStrings.openDialogButton}</Button> }
	</>

};
